import * as negotiationV2 from './transactionProcessNegotiationV2';

const {
  AUTO_CANCEL,
  OPERATOR_CANCEL,
  DELIVER,
  OPERATOR_MARK_DELIVERED,
  REQUEST_CHANGES,
  OPERATOR_REQUEST_CHANGES,
  DELIVER_CHANGES,
  OPERATOR_MARK_CHANGES_DELIVERED,
  OPERATOR_CANCEL_FROM_DELIVERED,
  AUTO_CANCEL_FROM_CHANGES_REQUESTED,
  OPERATOR_CANCEL_FROM_CHANGES_REQUESTED,
  ACCEPT_DELIVERABLE,
  AUTO_ACCEPT_DELIVERABLE,
  OPERATOR_ACCEPT_DELIVERABLE,
  ...preConfirmationTransitions
} = negotiationV2.transitions;

export const transitions = {
  ...preConfirmationTransitions,
  PROVIDER_MARK_COMPLETED: 'transition/provider-mark-completed',
  EXPIRE_PROVIDER_COMPLETION: 'transition/expire-provider-completion',
  CUSTOMER_CONFIRM_COMPLETION: 'transition/customer-confirm-completion',
  AUTO_CONFIRM_COMPLETION: 'transition/auto-confirm-completion',
  CUSTOMER_REPORT_PROBLEM: 'transition/customer-report-problem',
  CUSTOMER_REPORT_PROBLEM_TO_RESUELVEPR: 'transition/customer-report-problem-to-resuelvepr',
  PROVIDER_MARK_CORRECTION_COMPLETED: 'transition/provider-mark-correction-completed',
  CUSTOMER_ESCALATE_PROBLEM_TO_RESUELVEPR: 'transition/customer-escalate-problem-to-resuelvepr',
  CUSTOMER_CONFIRM_CORRECTED_SERVICE: 'transition/customer-confirm-corrected-service',
  AUTO_CONFIRM_CORRECTED_SERVICE: 'transition/auto-confirm-corrected-service',
  CUSTOMER_REPORT_CORRECTED_SERVICE_TO_RESUELVEPR:
    'transition/customer-report-corrected-service-to-resuelvepr',
  OPERATOR_REFUND_SERVICE_COMPLETION: 'transition/operator-refund-service-completion',
  OPERATOR_PAY_PROVIDER_SERVICE_COMPLETION: 'transition/operator-pay-provider-service-completion',
};

const { CHANGES_REQUESTED, COMPLETED, ...preConfirmationStates } = negotiationV2.states;

export const states = {
  ...preConfirmationStates,
  PROVIDER_COMPLETED: 'provider-completed',
  PROBLEM_REPORTED: 'problem-reported',
  PROVIDER_CORRECTION_COMPLETED: 'provider-correction-completed',
  COMPLETION_REVIEW: 'completion-review',
  COMPLETION_REFUNDED: 'completion-refunded',
  COMPLETION_PAID_TO_PROVIDER: 'completion-paid-to-provider',
  PROVIDER_COMPLETION_EXPIRED: 'provider-completion-expired',
};

const v2States = negotiationV2.graph.states;

export const graph = {
  id: 'default-negotiation/release-3',
  initial: states.INITIAL,
  states: {
    [states.INITIAL]: v2States[negotiationV2.states.INITIAL],
    [states.INQUIRY]: v2States[negotiationV2.states.INQUIRY],
    [states.QUOTE_REQUESTED]: v2States[negotiationV2.states.QUOTE_REQUESTED],
    [states.REQUEST_REJECTED]: { type: 'final' },
    [states.OFFER_PENDING]: v2States[negotiationV2.states.OFFER_PENDING],
    [states.UPDATE_PENDING]: v2States[negotiationV2.states.UPDATE_PENDING],
    [states.CUSTOMER_OFFER_PENDING]: v2States[negotiationV2.states.CUSTOMER_OFFER_PENDING],
    [states.OFFER_REJECTED]: v2States[negotiationV2.states.OFFER_REJECTED],
    [states.PENDING_PAYMENT]: v2States[negotiationV2.states.PENDING_PAYMENT],
    [states.PAYMENT_EXPIRED]: v2States[negotiationV2.states.PAYMENT_EXPIRED],
    [states.OFFER_ACCEPTED]: {
      on: {
        [transitions.CUSTOMER_CANCEL_BEFORE_SERVICE]: states.CANCELED,
        [transitions.CUSTOMER_CANCEL_SERVICE_DAY]: states.CANCELLATION_REVIEW,
        [transitions.PROVIDER_CANCEL_BEFORE_SERVICE]: states.CANCELED,
        [transitions.PROVIDER_MARK_COMPLETED]: states.PROVIDER_COMPLETED,
        [transitions.EXPIRE_PROVIDER_COMPLETION]: states.PROVIDER_COMPLETION_EXPIRED,
      },
    },
    [states.PROVIDER_COMPLETED]: {
      on: {
        [transitions.CUSTOMER_CONFIRM_COMPLETION]: states.DELIVERED,
        [transitions.AUTO_CONFIRM_COMPLETION]: states.DELIVERED,
        [transitions.CUSTOMER_REPORT_PROBLEM]: states.PROBLEM_REPORTED,
        [transitions.CUSTOMER_REPORT_PROBLEM_TO_RESUELVEPR]: states.COMPLETION_REVIEW,
      },
    },
    [states.PROBLEM_REPORTED]: {
      on: {
        [transitions.PROVIDER_MARK_CORRECTION_COMPLETED]: states.PROVIDER_CORRECTION_COMPLETED,
        [transitions.CUSTOMER_ESCALATE_PROBLEM_TO_RESUELVEPR]: states.COMPLETION_REVIEW,
      },
    },
    [states.PROVIDER_CORRECTION_COMPLETED]: {
      on: {
        [transitions.CUSTOMER_CONFIRM_CORRECTED_SERVICE]: states.DELIVERED,
        [transitions.AUTO_CONFIRM_CORRECTED_SERVICE]: states.DELIVERED,
        [transitions.CUSTOMER_REPORT_CORRECTED_SERVICE_TO_RESUELVEPR]: states.COMPLETION_REVIEW,
      },
    },
    [states.COMPLETION_REVIEW]: {
      on: {
        [transitions.OPERATOR_REFUND_SERVICE_COMPLETION]: states.COMPLETION_REFUNDED,
        [transitions.OPERATOR_PAY_PROVIDER_SERVICE_COMPLETION]: states.COMPLETION_PAID_TO_PROVIDER,
      },
    },
    [states.CANCELLATION_REVIEW]: v2States[negotiationV2.states.CANCELLATION_REVIEW],
    [states.DELIVERED]: {
      on: {
        [transitions.REVIEW_1_BY_PROVIDER]: states.REVIEWED_BY_PROVIDER,
        [transitions.REVIEW_1_BY_CUSTOMER]: states.REVIEWED_BY_CUSTOMER,
        [transitions.EXPIRE_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED_BY_CUSTOMER]: v2States[negotiationV2.states.REVIEWED_BY_CUSTOMER],
    [states.REVIEWED_BY_PROVIDER]: v2States[negotiationV2.states.REVIEWED_BY_PROVIDER],
    [states.REVIEWED]: { type: 'final' },
    [states.CANCELED]: { type: 'final' },
    [states.CANCELLATION_REFUNDED]: { type: 'final' },
    [states.CANCELLATION_PAID_TO_PROVIDER]: { type: 'final' },
    [states.PARTIALLY_REFUNDED]: { type: 'final' },
    [states.COMPLETION_REFUNDED]: { type: 'final' },
    [states.COMPLETION_PAID_TO_PROVIDER]: { type: 'final' },
    [states.PROVIDER_COMPLETION_EXPIRED]: { type: 'final' },
  },
};

const v3Transitions = [
  transitions.PROVIDER_MARK_COMPLETED,
  transitions.EXPIRE_PROVIDER_COMPLETION,
  transitions.CUSTOMER_CONFIRM_COMPLETION,
  transitions.AUTO_CONFIRM_COMPLETION,
  transitions.CUSTOMER_REPORT_PROBLEM,
  transitions.CUSTOMER_REPORT_PROBLEM_TO_RESUELVEPR,
  transitions.PROVIDER_MARK_CORRECTION_COMPLETED,
  transitions.CUSTOMER_ESCALATE_PROBLEM_TO_RESUELVEPR,
  transitions.CUSTOMER_CONFIRM_CORRECTED_SERVICE,
  transitions.AUTO_CONFIRM_CORRECTED_SERVICE,
  transitions.CUSTOMER_REPORT_CORRECTED_SERVICE_TO_RESUELVEPR,
  transitions.OPERATOR_REFUND_SERVICE_COMPLETION,
  transitions.OPERATOR_PAY_PROVIDER_SERVICE_COMPLETION,
];

export const isRelevantPastTransition = transition =>
  v3Transitions.includes(transition) || negotiationV2.isRelevantPastTransition(transition);

export const isPrivileged = transition =>
  [
    transitions.REQUEST_PAYMENT_TO_ACCEPT_OFFER,
    transitions.PROVIDER_MARK_COMPLETED,
    transitions.CUSTOMER_REPORT_PROBLEM,
    transitions.CUSTOMER_REPORT_PROBLEM_TO_RESUELVEPR,
    transitions.PROVIDER_MARK_CORRECTION_COMPLETED,
    transitions.CUSTOMER_ESCALATE_PROBLEM_TO_RESUELVEPR,
    transitions.CUSTOMER_REPORT_CORRECTED_SERVICE_TO_RESUELVEPR,
  ].includes(transition) || negotiationV2.isPrivileged(transition);

export const isCompleted = transition =>
  [
    transitions.CUSTOMER_CONFIRM_COMPLETION,
    transitions.AUTO_CONFIRM_COMPLETION,
    transitions.CUSTOMER_CONFIRM_CORRECTED_SERVICE,
    transitions.AUTO_CONFIRM_CORRECTED_SERVICE,
    transitions.OPERATOR_PAY_PROVIDER_SERVICE_COMPLETION,
  ].includes(transition) || negotiationV2.isCompleted(transition);

export const isRefunded = transition =>
  [transitions.EXPIRE_PROVIDER_COMPLETION, transitions.OPERATOR_REFUND_SERVICE_COMPLETION].includes(
    transition
  ) || negotiationV2.isRefunded(transition);

export const isCustomerReview = negotiationV2.isCustomerReview;
export const isProviderReview = negotiationV2.isProviderReview;
export const getTransitionsWithMatchingOffers = negotiationV2.getTransitionsWithMatchingOffers;
export const isValidNegotiationOffersArray = negotiationV2.isValidNegotiationOffersArray;
export const isNegotiationState = negotiationV2.isNegotiationState;

export const statesNeedingProviderAttention = [states.OFFER_ACCEPTED, states.PROBLEM_REPORTED];
export const statesNeedingCustomerAttention = [
  states.OFFER_PENDING,
  states.PROVIDER_COMPLETED,
  states.PROVIDER_CORRECTION_COMPLETED,
];
